import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components/macro';
import { JoinButton } from 'src/client/components/buttons/JoinButton';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { useSelector } from 'src/client/redux';
import { SPECIAL_FEATURES } from 'src/client/constants/joinCampaigns';

type Props = {
  className?: string;
  hideSignUp?: boolean;
};

export const SpecialFeaturesSection = (props: Props) => {
  const { className = '', hideSignUp = false } = props;
  const { cdnUrl } = useSelector((state) => state.app);
  const { UUID } = useSelector((state) => state.site);
  const showSfwSite = useShowSfwSite();

  const sfwFolder = showSfwSite ? 'sfw' : '';
  const basePath = [cdnUrl, 'splash', UUID, sfwFolder].filter(Boolean).join('/');

  return (
    <SectionRoot className={`${className} special-features`} hideSignUp={hideSignUp}>
      <div className="container">
        <Title>Special Features: Inside ALS World</Title>
        <Content>
          <Picks>
            <PickLink to="/category/Director_Picks">
              <Colorized className="director">ALS/Director Picks</Colorized>
              <img src={`${basePath}/category_director.jpg`} alt="ALS/Director Picks" />
            </PickLink>
            <PickLink to="/category/Vacations">
              <Colorized className="vacations">ALS/Vacations</Colorized>
              <img src={`${basePath}/category_vacation.jpg`} alt="ALS/Vacations" />
            </PickLink>
            <PickLink to="/category/Casting_Calls">
              <Colorized className="casting">ALS/Casting Calls</Colorized>
              <img src={`${basePath}/category_casting.jpg`} alt="ALS/Casting Calls" />
            </PickLink>
            <PickLink to="/category/Productions">
              <Colorized className="productions">ALS/Productions</Colorized>
              <img src={`${basePath}/category_production.jpg`} alt="ALS/Productions" />
            </PickLink>
          </Picks>
          {!hideSignUp && (
            <Access>
              <AccessSection>
                <ul>
                  <li>1000's of starlets</li>
                  <li>Create favorites</li>
                  <li>Unlimited downloads</li>
                </ul>
                <ul>
                  <li>500,000 images</li>
                  <li>1000&apos;s of movies</li>
                  <li>Sexy live cams</li>
                </ul>
              </AccessSection>
              <AccessSection>
                <JoinAction campaign={SPECIAL_FEATURES} className="btn btn-primary">
                  Sign Up Today
                </JoinAction>
              </AccessSection>
              <AccessSection>
                <TourLink to="/updates">&gt;&gt; Enjoy a free tour</TourLink>
              </AccessSection>
            </Access>
          )}
        </Content>
      </div>
    </SectionRoot>
  );
};

const SectionRoot = styled.section<{ hideSignUp: boolean }>`
  && {
    background-color: ${(props) => (!props.hideSignUp ? props.theme.splashOptional1 : 'inherit')};
    padding: 36px 0;
  }
`;

const Title = styled.div`
  && {
    color: ${(props) => props.theme.splashPrimary1};
    background-color: ${(props) => props.theme.primary9};
    display: inline-block;
    font-size: 1.72rem;
    padding: 12px 24px;
    text-transform: uppercase;
  }
`;

const Content = styled.div`
  && {
    background-color: ${(props) => props.theme.splashPrimary1};

    @media (min-width: 768px) {
      padding: 24px;
    }
  }
`;

const Picks = styled.div`
  && {
    display: flex;
    flex-direction: column;
    padding-top: 12px;

    @media (min-width: 568px) {
      flex-direction: row;
      flex-flow: wrap;
    }

    @media (min-width: 768px) {
      flex-flow: unset;
    }
  }
`;

const PickLink = styled(NavLink)`
  && {
    margin: 0 8px 8px;
    padding-top: 0;
    width: calc(100% - 16px);

    @media (min-width: 568px) and (max-width: 767px) {
      width: calc(50% - 16px);
    }

    @media (min-width: 768px) {
      margin: unset;
      flex-grow: 1;
      flex-basis: 0;
      width: 100%;
    }

    &:not(:last-child) {
      @media (min-width: 768px) {
        margin-right: 8px;
      }
    }

    img {
      width: 100%;
    }
  }
`;

const Colorized = styled.div`
  color: ${(props) => props.theme.splashPrimary1};
  display: block;
  font-size: 1.15rem;
  padding: 8px;

  @media (min-width: 1024px) {
    font-size: 1.3rem;
  }

  &.director {
    background-color: ${(props) => props.theme.primary9};
  }
  &.vacations {
    background-color: ${(props) => props.theme.primary10};
  }
  &.casting {
    background-color: ${(props) => props.theme.primary12};
  }
  &.productions {
    background-color: ${(props) => props.theme.primary11};
  }
`;

const Access = styled.div`
  && {
    display: flex;
    flex-direction: column;
    margin-top: 4px;

    @media (min-width: 768px) {
      flex-flow: unset;
    }
  }
`;

const AccessSection = styled.section`
  && {
    background-color: ${(props) => props.theme.splashOptional1};
    width: 100%;

    @media (min-width: 768px) {
      flex-grow: 1;
      flex-basis: 0;
      padding: 36px 0;
    }

    ul {
      margin: 0 12px;
      padding: 0;
      text-align: center;
      text-transform: uppercase;
      width: 100%;

      @media (min-width: 414px) and (max-width: 767px) {
        float: left;
        text-align: unset;
        width: calc(50% - 24px);
      }

      @media (min-width: 1200px) {
        float: left;
        text-align: unset;
        width: unset;
      }

      li {
        list-style-type: none;
        margin: 4px 0;
      }
    }

    &:not(:last-child) {
      @media (min-width: 768px) {
        margin-right: 8px;
      }
    }
  }
`;

const JoinAction = styled(JoinButton)`
  && {
    color: ${(props) => props.theme.splashPrimary1};
    display: block;
    font-size: 1.4rem;
    margin: 0 24px;
    padding: 12px 0;
    position: relative;

    @media (min-width: 768px) {
      top: 50%;
      transform: translateY(-50%);
    }
  }
`;

const TourLink = styled(NavLink)`
  && {
    display: block;
    color: ${(props) => props.theme.splashPrimary2};
    font-size: 1.15rem;
    position: relative;
    text-align: center;
    text-transform: uppercase;

    @media (min-width: 768px) {
      top: 50%;
      transform: translateY(-50%);
    }

    @media (min-width: 1200px) {
      font-size: 2rem;
    }
  }
`;
